import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { XIcon } from '@heroicons/react/outline';

/**
 * TriageConfirmDialog
 * -------------------
 * A short confirm step for a triage action, built on the same dialog shell,
 * title style and button pair as SignOffTriageLetterDialog:
 *
 *   - "Check your decision" before a triage decision is recorded (TDI-693).
 *   - "Change your decision?" before a clinician reopens their own decision
 *     (TDI-695).
 *
 * Nothing happens until the confirm button is pressed. The cancel button, the
 * close button and Esc all close the dialog and change nothing; the caller
 * keeps whatever state it had (the decision wizard keeps every answer).
 *
 * Props:
 *   - open, onOpenChange: controlled open state
 *   - title: the dialog title
 *   - subtitle?: one line under the title (for example the pathway)
 *   - lines: the body, one paragraph per entry
 *   - cancelLabel, confirmLabel: the button pair
 *   - busy?: disables the confirm button while the action is in flight
 *   - onConfirm: called when the confirm button is pressed
 */
export default function TriageConfirmDialog({
  open,
  onOpenChange,
  title,
  subtitle = null,
  lines = [],
  cancelLabel,
  confirmLabel,
  busy = false,
  onConfirm,
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/40 backdrop-blur-sm z-50" />
        <Dialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90vw] max-w-md bg-white rounded-xl shadow-lg z-50 overflow-hidden">
          <div className="flex items-start justify-between p-5 pb-3">
            <div className="text-left">
              <Dialog.Title className="text-base font-semibold text-gray-900">{title}</Dialog.Title>
              {subtitle ? (
                <Dialog.Description className="text-sm text-gray-500 mt-0.5">{subtitle}</Dialog.Description>
              ) : (
                <Dialog.Description className="sr-only">{title}</Dialog.Description>
              )}
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                aria-label="Close"
              >
                <XIcon className="w-5 h-5" />
              </button>
            </Dialog.Close>
          </div>

          <div className="px-5 pb-2 text-left space-y-2">
            {lines.map((line) => (
              <p key={line} className="text-sm text-gray-700">{line}</p>
            ))}
          </div>

          <div className="flex justify-end gap-2 p-5 pt-3">
            <Dialog.Close asChild>
              <button
                type="button"
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
              >
                {cancelLabel}
              </button>
            </Dialog.Close>
            <button
              type="button"
              disabled={busy}
              onClick={() => onConfirm && onConfirm()}
              className="px-5 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {confirmLabel}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
